<template>
  <div class="box frp-graph-card critical-card" :class="{ 'is-compact': compact }">
    <div class="frp-card-header">
      <b-icon icon="alert-octagon-outline" class="mr-2" />
      <h3 class="title is-6 mb-0">Casos críticos</h3>
      <span
        class="critical-info"
        v-tooltip.bottom="{
          content: criteriaTooltip,
          classes: ['critical-tooltip'],
          html: true,
        }"
      >
        <i class="material-icons">info_outline</i>
      </span>
    </div>
    <hr class="frp-card-divider" />

    <!-- Empty state -->
    <div v-if="!total" class="critical-empty">
      <span class="is-flex">
        <i class="material-icons">insert_chart</i>&nbsp;No hay suficientes datos
      </span>
    </div>

    <!-- Content -->
    <div v-else class="critical-body">
      <!-- Highlight: actionable cases -->
      <div class="critical-highlight">
        <span class="critical-highlight__value">{{ actionable }}</span>
        <span class="critical-highlight__label">
          {{ actionable === 1 ? "caso requiere" : "casos requieren" }}
          vigilancia epidemiológica
        </span>
        <span class="critical-highlight__hint">Críticos + muy críticos</span>
      </div>

      <!-- Breakdown by level (acts as the pie legend). A brace hugs the two
           "critical" levels and points back at the red 95 on the left, tying
           30 + 65 to that total — instead of the bar-like row fill they used
           to have, which users misread as a bar chart. -->
      <div class="critical-breakdown">
        <div
          class="critical-brace-col"
          :style="{ height: actionableRows.length * 24 + 'px' }"
        >
          <svg
            class="critical-brace"
            viewBox="0 0 9 48"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M9,0 C4,0 5,3.6 5,9.6 C5,18 4,21.6 0,24 C4,26.4 5,30 5,38.4 C5,44.4 4,48 9,48"
              fill="none"
              stroke="#e0908e"
              stroke-width="1.4"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <div class="critical-rows">
          <div v-for="lvl in rows" :key="lvl.key" class="critical-row">
            <span
              class="critical-dot"
              :style="{ background: lvl.color, borderColor: lvl.border }"
            ></span>
            <span class="critical-row__label">{{ lvl.label }}</span>
            <span
              class="critical-row__count"
              :class="{ 'is-actionable': lvl.actionable }"
            >
              {{ lvl.count }}
              <span class="critical-row__pct">({{ lvl.pct }}%)</span>
            </span>
          </div>
        </div>
      </div>

      <!-- Proportions pie (no legend — the breakdown above is the legend) -->
      <div class="critical-pie">
        <y-graph :datagraph="pieData" :cambio="cambio"></y-graph>
      </div>
    </div>
  </div>
</template>

<script>
import yGraph from "@/components/dashboard/GraphFrs.vue";

// Niveles de criticidad (Resolución 2646/2008). El orden y los colores reusan
// la paleta de riesgo del tablero (ver rangoFp en Resultados.vue).
const LEVELS = [
  {
    key: "Caso muy crítico",
    label: "Muy crítico",
    color: "#f4cdcd",
    border: "#f6adad",
    actionable: true,
  },
  {
    key: "Caso crítico",
    label: "Crítico",
    color: "#facca5",
    border: "#f6b884",
    actionable: true,
  },
  {
    key: "Caso medianamente crítico",
    label: "Medianamente crítico",
    color: "#fff2cc",
    border: "#f6db8a",
    actionable: false,
  },
  {
    key: "Caso no crítico",
    label: "No crítico",
    color: "#d9ead3",
    border: "#c3eab5",
    actionable: false,
  },
];

export default {
  name: "CriticalCasesCard",
  components: { yGraph },
  props: {
    // Distribución "RGO-12-CASOS CRÍTICOS": [{ SubGrupo, countData: { sum } }].
    data: {
      type: Array,
      default() {
        return [];
      },
    },
    // Se incrementa en el padre al filtrar; fuerza el redibujo del pie.
    cambio: {
      type: Number,
      default: 0,
    },
    // Modo compacto: cuando la tarjeta Forma muestra subtotales, esta queda más
    // angosta (is-5). Reduce el pie, el recuadro rojo y las etiquetas para caber.
    compact: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    counts() {
      const map = {};
      (this.data || []).forEach((x) => {
        if (x && x.SubGrupo) {
          map[x.SubGrupo] =
            (map[x.SubGrupo] || 0) +
            ((x.countData && x.countData.sum) || 0);
        }
      });
      return map;
    },
    total() {
      return Object.values(this.counts).reduce((a, b) => a + b, 0);
    },
    actionable() {
      return LEVELS.filter((l) => l.actionable).reduce(
        (acc, l) => acc + (this.counts[l.key] || 0),
        0,
      );
    },
    rows() {
      return LEVELS.map((l) => {
        const count = this.counts[l.key] || 0;
        const pct = this.total ? ((count * 100) / this.total).toFixed(1) : "0.0";
        return { ...l, count, pct };
      });
    },
    // Los niveles accionables (muy crítico + crítico): la llave los abraza y
    // apunta al total 95 de la izquierda. Se usa para dimensionar la llave.
    actionableRows() {
      return this.rows.filter((r) => r.actionable);
    },
    // Pie (Chart.js vía y-graph). Sin leyenda: el desglose de la izquierda ya
    // la cumple. Muestra el % sobre las porciones grandes.
    pieData() {
      return {
        type: "pie",
        data: {
          labels: LEVELS.map((l) => l.label),
          datasets: [
            {
              data: LEVELS.map((l) => this.counts[l.key] || 0),
              backgroundColor: LEVELS.map((l) => l.color),
              borderColor: LEVELS.map((l) => l.border),
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          resizeDelay: 0,
          legend: { display: false },
          tooltips: { enabled: true },
          layout: { padding: 2 },
          plugins: {
            datalabels: {
              formatter: (value, ctx) => {
                const arr = ctx.chart.data.datasets[0].data;
                const sum = arr.reduce((a, b) => a + Number(b), 0);
                if (!sum || !value) return "";
                return `${Math.round((value * 100) / sum)}%`;
              },
              color: "#4a4a4a",
              font: { size: 10, weight: "bold" },
            },
          },
        },
      };
    },
    criteriaTooltip() {
      return `
        <div class="critical-criteria">
          <strong>Casos críticos — Resolución 2646 de 2008 (Art. 16)</strong>
          <p>Nivel de criticidad según 5 indicadores: Intralaboral, Extralaboral,
          Condiciones Individuales, Burnout y Estrés.</p>
          <ul>
            <li><b>Muy crítico:</b> 4 o 5 indicadores entre riesgo alto y muy alto, y mínimo 2 en muy alto.</li>
            <li><b>Crítico:</b> 4 o 5 entre alto y muy alto con menos de 2 en muy alto; o 3 entre alto y muy alto con mínimo 1 en muy alto.</li>
            <li><b>Medianamente crítico:</b> 3 en riesgo alto, o 2 entre alto y muy alto; o el indicador intralaboral en alto.</li>
            <li><b>No crítico:</b> si no cumple ninguno de los criterios anteriores.</li>
          </ul>
        </div>`;
    },
  },
};
</script>

<style lang="scss" scoped>
$brand: #522687;

.critical-card {
  display: flex;
  flex-direction: column;
  /* Un poco más de aire horizontal que el .box por defecto (1.25rem). */
  padding-left: 1.75rem;
  padding-right: 1.75rem;
}

.frp-card-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.frp-card-header .icon {
  color: #c0392b;
}

.critical-info {
  margin-left: auto;
  color: #b5b5b5;
  cursor: help;
  display: inline-flex;

  .material-icons {
    font-size: 18px;
  }
}

.frp-card-divider {
  margin: 0.5rem 0 1rem;
  background-color: whitesmoke;
  height: 2px;
}

.critical-empty {
  color: #7a7a7a;
  font-size: 0.9rem;
}

.critical-body {
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 1.25rem;
  flex: 1 1 auto;
  min-height: 0;
}

/* Left: actionable highlight (alert look, reusing bg-alta palette) */
.critical-highlight {
  flex: 0 0 27%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
 
  background-color: #f4cdcd;
  border: 1px solid #f6adad;
  border-radius: 8px;
}

.critical-highlight__value {
  font-size: 1.7rem;
  font-weight: 700;
  line-height: 1;
  color: #a12a2a;
}

.critical-highlight__label {
  margin-top: 0.3rem;
  font-size: 0.7rem;
  line-height: 1.2;
  color: #7a3030;
}

.critical-highlight__hint {
  margin-top: 0.18rem;
  font-size: 0.64rem;
  color: #a1746f;
}

/* Middle: 4-level breakdown. A brace hugs the two "critical" rows and points
   left at the red 95, so 30+65 reads as that total — no bar-like row fill. */
.critical-breakdown {
  flex: 0 1 auto;
  align-self: center;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 6px;
  margin: 0;
}

/* Left gutter: the brace, sized to span exactly the two actionable rows
   (height set inline = rows × 24px) and pointing back at the highlight. */
.critical-brace-col {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  padding-right: 2px;
}

.critical-brace {
  width: 9px;
  height: 100%;
}

.critical-rows {
  display: flex;
  flex-direction: column;
}

.critical-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 24px;
  font-size: 0.8rem;
  color: #363636;
}

.critical-dot {
  flex: 0 0 auto;
  width: 11px;
  height: 11px;
  border-radius: 3px;
  border: 1px solid;
}

.critical-row__label {
  flex: 0 0 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.critical-row__count {
  flex: 0 0 72px;
  text-align: right;
  font-weight: 700;
}

/* Keep the red count color for the two actionable rows. */
.critical-row__count.is-actionable {
  color: #a12a2a;
}

/* Percentage equivalent: lighter, secondary to the absolute count. */
.critical-row__pct {
  font-weight: 400;
  font-size: 0.7rem;
  color: #9aa0a6;
}

.critical-row__count.is-actionable .critical-row__pct {
  color: #c58f8f;
}

/* Right: proportions pie (square so the pie stays circular) */
.critical-pie {
  flex: 0 0 100px;
  height: 100px;
  align-self: center;
  position: relative;
}

.critical-pie .chart-container {
  height: 100% !important;
}

/* Modo compacto (tarjeta angosta cuando Forma trae subtotales): encoge el pie,
   el recuadro rojo y las etiquetas del desglose para que todo quepa. */
.critical-card.is-compact {
  .critical-body {
    gap: 0.75rem;
  }

  .critical-highlight {
    flex-basis: 27%;
  }

  .critical-highlight__value {
    font-size: 1.7rem;
  }

  .critical-highlight__label {
    font-size: 0.68rem;
  }

  .critical-breakdown {
    gap: 4px;
  }

  .critical-row {
    font-size: 0.74rem;
    gap: 0.35rem;
  }

  .critical-row__label {
    flex-basis: 118px;
  }

  .critical-row__count {
    flex-basis: 62px;
  }

  .critical-pie {
    flex: 0 0 96px;
    height: 96px;
  }
}

@media (max-width: 768px) {
  .critical-body {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .critical-highlight {
    flex: 0 0 auto;
  }

  .critical-pie {
    align-self: center;
  }
}
</style>

<!-- Non-scoped: v-tooltip renders at <body>, so the criteria tooltip theme
     must be global. -->
<style lang="scss">
.critical-tooltip {
  max-width: 340px !important;

  .tooltip-inner {
    background: rgba(0, 0, 0, 0.9);
    text-align: left;
  }
  .tooltip-arrow {
    border-color: rgba(0, 0, 0, 0.9);
  }

  .critical-criteria {
    font-size: 12px;
    line-height: 1.35;

    strong {
      color: #fff;
    }
    p {
      margin: 6px 0;
    }
    ul {
      margin: 0;
      padding-left: 16px;
      list-style: disc;
    }
    li {
      margin-bottom: 4px;
    }
    b {
      color: #fff;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/frpm/components/CriticalCasesCard.vue